import React, { useRef, useState, useEffect } from 'react';
import { isMobile } from 'react-device-detect';

import { GradientFilledButton } from '@magoosh/lib/button';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { outputDeviceId } from '@magoosh/organisms/audio/audio_devices';
import { loadVolume, persistVolume } from '@magoosh/organisms/audio/volume';

import StimulusControlsAndSettings, { Settings } from '../components/stimulus_controls_and_settings';

interface Props {
  compatibilityMode: boolean;
  direction?: string;
  isAnswered: boolean;
  noAutoplay?: boolean;
  onChangeCompatibilityMode?: (compatibilityMode: boolean) => void;
  onChangeVolume?: (volume: number) => void;
  onFinishPlayback?: () => void;
  onResetPlayback?: (compatibilityMode: boolean) => void;
  playButtonText?: string;
  referenceImageSrc: string;
  src: string;
}

export default (props: Props) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [compatibilityMode, setCompatabilityMode] = useState<boolean>(props.compatibilityMode);
  const [autoplayFailed, setAutoplayFailed] = useState<boolean>(false);
  const [audioHasFinished, setAudioHasFinished] = useState<boolean>(false);
  const [audioHasStarted, setAudioHasStarted] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(1);

  useEffect(() => {
    if (props.isAnswered) return;
    if (compatibilityMode) return;

    attemptAutoplay();
  }, []);

  useEffect(() => {
    const savedVolume = loadVolume();
    setVolume(savedVolume);

    if (!audioRef.current) return;

    audioRef.current.volume = savedVolume;

    const handleVolumeChange = () => {
      const newVolume = audioRef.current.muted ? 0 : audioRef.current.volume;
      setVolume(newVolume);
      persistVolume(newVolume);
      props.onChangeVolume?.(newVolume);
    };

    const handlePlay = () => {
      if (audioRef.current && 'setSinkId' in audioRef.current) {
        (audioRef.current as any).setSinkId(outputDeviceId()).catch(() => {});
      }
    };

    audioRef.current.addEventListener('volumechange', handleVolumeChange);
    audioRef.current.addEventListener('play', handlePlay);

    return () => {
      audioRef.current?.removeEventListener('volumechange', handleVolumeChange);
      audioRef.current?.removeEventListener('play', handlePlay);
    };
  }, []);

  const attemptAutoplay = async () => {
    if (!audioRef.current) return;

    if (props.noAutoplay || isMobile) {
      setAutoplayFailed(true);
      return;
    }

    setAutoplayFailed(false);
      
    // Wait to start playback to not be so sudden
    setTimeout(async () => {
      setAudioHasStarted(true);

      audioRef.current.play().catch(() => {
        setAutoplayFailed(true);
        setAudioHasStarted(false);
      });
    }, 1000);
  };

  const handleSettingsChanged = (settings: Settings) => {
    setCompatabilityMode(settings.compatibilityMode);
    props.onChangeCompatibilityMode?.(settings.compatibilityMode);

    resetQuestion(settings.compatibilityMode);

    if (!settings.compatibilityMode && !props.isAnswered) {
      attemptAutoplay();
    }
  };

  const handleVolumeChanged = (newValue: number) => {
    setVolume(newValue);
    persistVolume(newValue);
    props.onChangeVolume?.(newValue);
    audioRef.current.volume = newValue;
  };

  const resetQuestion = (compatibilityMode: boolean) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    props.onResetPlayback?.(compatibilityMode);
    setAutoplayFailed(true);
    setAudioHasFinished(false);
    setAudioHasStarted(false);
  };

  return (
    <>
      <div className="align-items-center d-flex flex-column gap-m u-margin-B-s u-padding-A-m well well-hollow">
        <StimulusControlsAndSettings
          compatibilityMode={compatibilityMode}
          onChangeVolume={handleVolumeChanged}
          onShowSettings={() => resetQuestion(compatibilityMode)}
          onSave={handleSettingsChanged}
          volume={volume}
        />

        {
          props.direction && (
            <div className="h4 text-bold text-center u-margin-A-n">
              {props.direction}
            </div>
          )
        }

        <div>
          <img alt="" src={props.referenceImageSrc} />
        </div>

        {
          !compatibilityMode && !props.isAnswered && (
            <GradientFilledButton
              disabled={audioHasStarted || !autoplayFailed}
              onClick={() => {
                setAudioHasStarted(true);
                audioRef.current.play().catch(() => { setAudioHasStarted(false) });
              }}
            >
              {(() => {
                if (audioHasFinished) {
                  return 'Audio Has Finished Playing';
                } else if (audioHasStarted) {
                  return 'Audio Is Playing';
                } else if (!autoplayFailed) {
                  return props.playButtonText || 'Play Audio';
                } else {
                  return <strong>{props.playButtonText || 'Play Audio'}</strong>;
                }
              })()}
            </GradientFilledButton>
          )
        }

        <audio
          ref={audioRef}
          controls={compatibilityMode || props.isAnswered}
          style={{ display: compatibilityMode || props.isAnswered ? 'block' : 'none' }}
          onEnded={() => {
            setAudioHasFinished(true);
            !props.isAnswered && props.onFinishPlayback?.();
          }}
        >
          <source src={props.src} />
          Your browser does not support the audio element.
        </audio>

        {
          compatibilityMode && (
            <div className="small text-center">
              On the actual exam, the audio will play automatically and can only be listened to once.
            </div>
          )
        }
      </div>
    </>
  );
};
